import { Dispatch, useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { ISignSubjectInfo } from 'APIPackage/KnmModule/interfaces/ISignSubjectInfo';
import { coreSetLoading } from 'CorePackage/MainModule/actions/coreActions';
import { NoticeTypesEnum } from 'CorePackage/NotificationModule/enums/NoticeTypesEnum';
import { MessageTypesEnum } from 'CorePackage/NotificationModule/enums/MessageTypesEnum';
import { addNoticeAction } from 'CorePackage/NotificationModule/actions/NoticeActions';
import { IKnm } from 'APIPackage/KnmModule/interfaces/IKnm';

interface IUseKnmFormSignHelpersProps {
  knmData?: IKnm;
  isKnmStatusFillInProcess?: boolean;
  isKnmStatusReadyForApprove?: boolean;
}

export const useKnmFormSignHelpers = ({
  knmData,
  isKnmStatusFillInProcess,
  isKnmStatusReadyForApprove,
}: IUseKnmFormSignHelpersProps | undefined = {}) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();

  /**
   * Парсинг информации из сертификата
   */
  const parseSubjectName = useCallback((str: string) => {
    const regex = /[A-Z]*?=/gm;

    let m;

    const objs: { key: string; value: string; position: number }[] = [];

    // eslint-disable-next-line no-cond-assign
    while ((m = regex.exec(str)) !== null) {
      if (m.index === regex.lastIndex) {
        // eslint-disable-next-line no-plusplus
        regex.lastIndex++;
      }

      m.forEach(key => {
        objs.push({ key, value: '', position: str.indexOf(key) });
      });
    }

    const removeQuotes = (value: string) => {
      if (value[0] === '"' && value[value.length - 1] === '"') {
        return value.substring(1, value.length - 1);
      }

      return value;
    };

    return objs.map((obj, i) => {
      const newObj = obj;

      if (i + 1 < objs.length) {
        newObj.value = str.substring(obj.position + obj.key.length, objs[i + 1].position);

        if (newObj.value[newObj.value.length - 1] === ' ' && newObj.value[newObj.value.length - 2] === ',') {
          newObj.value = newObj.value.substring(0, newObj.value.length - 2);
        }

        newObj.value = removeQuotes(newObj.value);
        newObj.key = newObj.key.replace('=', '');

        return newObj;
      }
      newObj.value = str.substring(obj.position + obj.key.length);
      newObj.value = removeQuotes(newObj.value);
      newObj.key = newObj.key.replace('=', '');

      return newObj;
    });
  }, []);

  /**
   * Функция извлечения из сертификата ФИО и организации
   */
  const getSubjectInfo = useCallback((subjectName: string) => {
    const array = parseSubjectName(subjectName);

    const org = array.find(i => i.key === 'O')?.value;
    const fullname = array.find(i => i.key === 'CN')?.value;
    const surename = array.find(i => i.key === 'SN')?.value;
    const givenname = array.find(i => i.key === 'G')?.value;

    return { fullname, org, surename, givenname } as ISignSubjectInfo;
  }, []);

  const checkErknmExtract = useCallback(
    (abort: () => void, proceed: () => void) => {
      dispatch(coreSetLoading(true));
      const extractDocuments = knmData?.documents?.filter(doc => doc?.type?.code === 'VP_VII');

      if (
        (isKnmStatusFillInProcess || isKnmStatusReadyForApprove) &&
        extractDocuments &&
        extractDocuments?.length > 0 &&
        !extractDocuments.find(doc => doc.active)
      ) {
        dispatch(coreSetLoading(false));
        dispatch(
          addNoticeAction({
            renderContent:
              'В значения полей выписки были внесены изменения. Повторно сформируйте выписку и после этого подпишите паспорт',
            noticeType: NoticeTypesEnum.ERROR,
            messageType: MessageTypesEnum.PASSPORT_SIGN,
          }),
        );
        abort();
      } else {
        dispatch(coreSetLoading(false));
        proceed();
      }
    },
    [knmData, isKnmStatusFillInProcess, isKnmStatusReadyForApprove],
  );

  return {
    getSubjectInfo,
    checkErknmExtract,
  };
};
